import React from 'react';
import { RouteComponentProps } from 'react-router';
import { Link, Redirect } from 'react-router-dom';
import { DateTime } from 'luxon';

import { dateReviver, groupBy } from 'Utils';
import { BowlingStats, BowlingStatsComponent } from 'Components/Games/Bowling';
import { BubbleStats, BubbleStatsComponent } from 'Components/Games/Bubble';
import { RowingStats, RowingStatsComponent } from 'Components/Games/Rowing';
import { HoopsStats, HoopsStatsComponent } from 'Components/Games/Hoops';
import { SoccerStats, SoccerStatsComponent } from 'Components/Games/Soccer';
import {
  StompingStats,
  StompingStatsComponent
} from 'Components/Games/Stomping';

interface MatchParams {
  studentId?: string;
  week?: string;
}

interface WeeklyProps extends RouteComponentProps<MatchParams> {}

interface Student {
  studentId: number;
  name: string;
  headshotUrl: string;
  creationDate: DateTime;
}

interface Recommendation {
  game: string;
  reason: string;
}

interface Session {
  gameSessionId: number;
  assisted: string;
  dateTime: DateTime;
  score: number;
  gameName: string;
  gameSlug: string;
}

interface Games {
  bubble?: BubbleStats[];
  bowling?: BowlingStats[];
  hoops?: HoopsStats[];
  rowing?: RowingStats[];
  soccer?: SoccerStats[];
  stomping?: StompingStats[];
}

interface WeeklyReport {
  student: Student;
  weekStart: DateTime;
  weekEnd: DateTime;
  nextWeek: DateTime;
  prevWeek: DateTime;

  sessions: Session[];
  recommendations: Recommendation[];
  games: Games;
}

interface GameGroupProps {
  game: string;
  sessions: Session[];
}

function formatGameName(game: string) {
  switch (game) {
    case 'rowing':
      return 'Rowing';
    case 'bowling':
      return 'Bowling';
    case 'bubble':
      return 'Bubble';
    case 'hoops':
      return 'Hoops';
    case 'stomping':
      return 'Stomping';
    case 'soccer':
      return 'Soccer';
    default:
      return '';
  }
}

const GameGroup: React.FC<GameGroupProps> = ({ game, sessions }) => {
  if (!sessions) return null;
  return (
    <div className='card'>
      <div className='card-body'>
        <h4 className='card-title'>{formatGameName(game)}</h4>
        <div className='card-text'>
          {sessions && (
            <table className='table table-bordered'>
              <thead>
                <tr>
                  <th scope='col'>Date</th>
                  <th scope='col'>Score</th>
                </tr>
              </thead>
              <tbody>
                {sessions
                  .sort((a, b) => a.dateTime.toMillis() - b.dateTime.toMillis())
                  .map(session => (
                    <tr key={session.gameSessionId}>
                      <td>{session.dateTime.toFormat('EEEE hh:mm a')}</td>
                      <td>{session.score.toLocaleString()}</td>
                      <td>
                        <a
                          href={`/report/session/${session.gameSessionId}`}
                          className='btn btn-info'
                        >
                          Full Report
                        </a>
                      </td>
                    </tr>
                  ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
};

interface GameStatsProps {
  games: Games;
}

const GameStats: React.FC<GameStatsProps> = ({ games }) => {
  return (
    <div className='card-columns'>
      {games.bubble && <BubbleStatsComponent stats={games.bubble} />}
      {games.bowling && <BowlingStatsComponent stats={games.bowling} />}
      {games.hoops && <HoopsStatsComponent stats={games.hoops} />}
      {games.rowing && <RowingStatsComponent stats={games.rowing} />}
      {games.soccer && <SoccerStatsComponent stats={games.soccer} />}
      {games.stomping && <StompingStatsComponent stats={games.stomping} />}
    </div>
  );
};

const cache: { [key: string]: WeeklyReport | { error: any } } = {};
const WeeklyComponent: React.FC<MatchParams> = ({ studentId, week }) => {
  if (!studentId) return null;
  if (!week)
    return (
      <Redirect
        to={`/student/${studentId}/weekly/${DateTime.local()
          .minus({ days: 7 })
          .startOf('week')
          .toISODate()}`}
      />
    );
  const result = cache[studentId + week];
  if (!result) {
    const promise = fetch(`/api-v2/report/weekly/${studentId}?week=${week}`, {
      headers: {
        'X-Requested-With': 'XMLHttpRequest'
      }
    })
      .then(r => {
        if (!r.ok) throw Error(r.statusText);
        return r;
      })
      .then(r => r.text())
      .then(r => JSON.parse(r, dateReviver))
      .then(r => (cache[studentId + week] = r))
      .catch(e => (cache[studentId + week] = { error: e }));
    throw promise;
  }

  const error = result as { error: any };
  if (error.error) {
    if (String(error.error).indexOf('Unauthorized') > 0) {
      window.location.href = `/Identity/Account/Login?ReturnUrl=/student/${studentId}/weekly/${week}`;
    }
    return (
      <div className='alert alert-danger'>
        <h1 className='alert-heading'>Error Occurred</h1>
        <p>{String(error.error)}</p>
      </div>
    );
  }

  const report = result as WeeklyReport;

  document.title = `${report.student.name} - Week ${report.weekStart.toFormat(
    'MM-dd-yyyy'
  )} - Flow Motion`;

  const games = ['bowling', 'bubble', 'hoops', 'rowing', 'soccer', 'stomping'];
  const sessions = groupBy(report.sessions, 'game');
  // const recommendations = groupBy(report.recommendations, 'game');

  return (
    <>
      <div className='card mb-4'>
        <div className='card-body'>
          <div className='row'>
            <div className='col-lg-1 img-fluid'>
              <img
                alt={report.student.name}
                className='rounded-circle'
                src={report.student.headshotUrl}
                width='80'
              />
            </div>
            <div className='col-lg-11'>
              <h3>
                <a href={`/student/${report.student.studentId}`}>
                  {report.student.name}
                </a>
              </h3>
              <div>
                <div>
                  <Link
                    to={`/student/${
                      report.student.studentId
                    }/weekly/${report.prevWeek.toISODate()}`}
                  >
                    Previous Week
                  </Link>
                </div>
                <div>
                  <Link
                    to={`/student/${
                      report.student.studentId
                    }/weekly/${report.nextWeek.toISODate()}`}
                  >
                    Next Week
                  </Link>
                </div>
                Results for the week of {report.weekStart.toISODate()}
              </div>
            </div>
          </div>
        </div>
      </div>
      {report.recommendations && (
        <div className='alert alert-info' role='alert'>
          <h5 className='alert-heading'>Recommendations</h5>
          {report.recommendations.map((r, i) => (
            <div key={i}>{r.reason}</div>
          ))}
        </div>
      )}
      <h1>Detailed Game Statistics</h1>
      <GameStats games={report.games} />
      <h1>Game Sessions</h1>
      <div className='card-columns'>
        {games.map(game => (
          <GameGroup
            key={game}
            game={game}
            sessions={sessions[game]}
            // recommendations={recommendations[game]}
          />
        ))}
      </div>
    </>
  );
};

const StudentWeeklyReport: React.FC<WeeklyProps> = ({ match }) => {
  return (
    <React.Suspense fallback='Loading...'>
      <WeeklyComponent {...match.params} />
    </React.Suspense>
  );
};

export default StudentWeeklyReport;
